import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import './SalonAdvancePayments.css';

// Advance Payments — mirrors the mobile app's Advance Payments screen. Every ₹11
// booking advance collected via Razorpay lands in the Ulloor Styles account and is
// paid out to the salon later; this page groups those advances by the customer's
// appointment date and shows whether Ulloor Styles has paid the salon yet. Data is
// read-only here — settlement flags are flipped by the Ulloor Styles admin dashboard.

const ADVANCE_AMOUNT = 11;

// Only a completed service or a customer no-show is payable to the salon — the ₹11
// advance is non-refundable either way, but cancelled/expired/upcoming bookings never
// owe the salon anything. Matches the admin Settlements page and the mobile app.
const isPayableBookingStatus = (status) => status === 'completed' || status === 'no_show';

// Mirrors src/utils/advanceStatusLabels.js in the mobile app — a raw 'confirmed'
// bookingStatus reads as "Upcoming" here since the appointment hasn't happened yet.
const BOOKING_STATUS_LABEL = {
  completed: 'Completed',
  no_show: 'No show',
  confirmed: 'Upcoming',
  cancelled_by_customer: 'Cancelled by customer',
  cancelled_by_salon: 'Cancelled by you',
  expired: 'Expired',
  rescheduled: 'Rescheduled',
  unknown: 'Unknown',
};

const formatDayLabel = (dayStr) => {
  if (!dayStr) return 'Unknown date';
  const [year, month, day] = String(dayStr).split('-').map(Number);
  const d = new Date(year, (month || 1) - 1, day || 1);
  if (Number.isNaN(d.getTime())) return dayStr;
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

const formatSettledDate = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Rows are grouped by appointment date, not collection date, so the row itself must
// spell out when the advance was actually paid — a bare time would read as if it
// belonged to the section's (appointment) date.
const formatPaidDateTime = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return '';
  const datePart = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  const timePart = d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
  return `${datePart}, ${timePart}`;
};

function SalonAdvancePayments() {
  const { salonData } = useAuth();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // bookingReference -> bookingStatus, so the salon can see which advances are
  // actually payable (completed/no-show) vs never will be (cancelled/expired/etc).
  const [bookingStatusByRef, setBookingStatusByRef] = useState({});

  const fetchPayments = useCallback(async () => {
    if (!salonData?.salonId) return;
    try {
      setLoading(true);
      setError('');
      const txnQuery = query(
        collection(db, 'paymentTransactions'),
        where('salonId', '==', salonData.salonId),
        where('status', '==', 'paid'),
        orderBy('paidAt', 'desc')
      );
      const snapshot = await getDocs(txnQuery);
      const rows = [];
      snapshot.forEach((docSnap) => rows.push({ id: docSnap.id, ...docSnap.data() }));
      setPayments(rows);

      // Fetch the current bookingStatus for every booking referenced by these
      // transactions. Scoped by salonId (in addition to bookingReference) because
      // Firestore rules only let a salon read its own bookings, and a list query
      // must be constrained enough to prove every possible match is allowed.
      const bookingRefs = [...new Set(rows.map((t) => t.bookingReference).filter(Boolean))];
      const statusEntries = [];
      for (let i = 0; i < bookingRefs.length; i += 30) {
        const chunk = bookingRefs.slice(i, i + 30);
        try {
          const bookingsQuery = query(
            collection(db, 'bookings'),
            where('salonId', '==', salonData.salonId),
            where('bookingReference', 'in', chunk)
          );
          const bookingsSnap = await getDocs(bookingsQuery);
          bookingsSnap.forEach((docSnap) => {
            const data = docSnap.data();
            statusEntries.push([data.bookingReference, data.bookingStatus || 'unknown']);
          });
        } catch (err) {
        }
      }
      setBookingStatusByRef(Object.fromEntries(statusEntries));
    } catch (err) {
      setError('Failed to load advance payments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [salonData?.salonId]);

  useEffect(() => {
    // Advance Pay is mandatory for all salons — always load the history directly.
    fetchPayments();
  }, [fetchPayments]);

  // Group by the appointment's IST calendar day, not the day the advance was collected —
  // a customer can book up to 6 days ahead, so those dates routinely differ. The query
  // itself is ordered by paidAt desc, so appointment days don't come in sorted order;
  // re-sort the sections newest-appointment-first once grouping is done.
  const sections = [];
  const sectionByDay = {};
  for (const payment of payments) {
    const day = payment.bookingDateLocal || (payment.bookingDate ? String(payment.bookingDate).split('T')[0] : 'unknown');
    if (!sectionByDay[day]) {
      sectionByDay[day] = { day, rows: [] };
      sections.push(sectionByDay[day]);
    }
    sectionByDay[day].rows.push(payment);
  }
  sections.sort((a, b) => b.day.localeCompare(a.day));
  sections.forEach((s) => {
    // "Collected" total — every advance received that day, regardless of payability.
    s.total = s.rows.reduce((sum, t) => sum + (Number(t.amount) || ADVANCE_AMOUNT), 0);
    s.payableRows = s.rows.filter((t) => isPayableBookingStatus(bookingStatusByRef[t.bookingReference] || 'unknown'));
    s.hasNothingPayable = s.payableRows.length === 0;
    s.allSettled = s.payableRows.length > 0 && s.payableRows.every((t) => t.settled === true);
    s.settledAt = s.allSettled
      ? s.payableRows.reduce((max, t) => (t.settledAt && t.settledAt > max ? t.settledAt : max), '')
      : null;
  });

  const payableRows = payments.filter((t) => isPayableBookingStatus(bookingStatusByRef[t.bookingReference] || 'unknown'));
  const notPayableCount = payments.length - payableRows.length;
  const pendingAmount = payableRows
    .filter((t) => t.settled !== true)
    .reduce((sum, t) => sum + (Number(t.amount) || ADVANCE_AMOUNT), 0);

  if (loading) {
    return (
      <div className="salon-advance-page">
        <div className="advance-loading">Loading advance payments...</div>
      </div>
    );
  }

  return (
    <div className="salon-advance-page">
      <div className="page-header">
        <h1>Advance Payments</h1>
        <p>₹{ADVANCE_AMOUNT} booking advances your customers paid online, and Ulloor Styles' payouts to you</p>
      </div>

      {error && <div className="message error">{error}</div>}

      {payments.length > 0 && (
        <div className="advance-summary">
          <div className="advance-summary-card">
            <div className={`advance-summary-value ${pendingAmount > 0 ? 'pending' : 'settled'}`}>
              {pendingAmount > 0 ? `₹${pendingAmount}` : 'All paid ✓'}
            </div>
            <div className="advance-summary-label">
              {pendingAmount > 0 ? 'Awaiting payout from Ulloor Styles' : 'Every advance has been paid to you'}
            </div>
          </div>
          {notPayableCount > 0 && (
            <div className="advance-summary-card">
              <div className="advance-summary-value na">{notPayableCount}</div>
              <div className="advance-summary-label">Not payable (cancelled, expired, etc.)</div>
            </div>
          )}
        </div>
      )}

      {payments.length === 0 ? (
        <div className="advance-empty">
          <div className="advance-empty-icon">💳</div>
          <h3>No advance payments yet</h3>
          <p>
            When customers pay the ₹{ADVANCE_AMOUNT} booking advance, every payment and its
            payout status will appear here.
          </p>
        </div>
      ) : (
        sections.map((section) => (
          <div key={section.day} className="advance-day-group">
            <div className="advance-day-header">
              <div className="advance-day-info">
                <span className="advance-day-label">{formatDayLabel(section.day)}</span>
                <span className="advance-day-total">
                  {section.rows.length} advance{section.rows.length > 1 ? 's' : ''} · ₹{section.total}
                </span>
              </div>
              {section.hasNothingPayable ? (
                <span className="advance-pill na">Nothing payable this day</span>
              ) : section.allSettled ? (
                <span className="advance-pill settled">
                  ✓ Paid by Ulloor Styles{section.settledAt ? ` on ${formatSettledDate(section.settledAt)}` : ''}
                </span>
              ) : (
                <span className="advance-pill pending">⏳ Pending from Ulloor Styles</span>
              )}
            </div>
            <table className="advance-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Booking Ref</th>
                  <th>Booking Status</th>
                  <th>Booked On</th>
                  <th>Amount</th>
                  <th>Payout</th>
                </tr>
              </thead>
              <tbody>
                {section.rows.map((txn) => {
                  const bookingStatus = bookingStatusByRef[txn.bookingReference] || 'unknown';
                  const payable = isPayableBookingStatus(bookingStatus);
                  return (
                    <tr key={txn.id}>
                      <td>{txn.customerName || 'Customer'}</td>
                      <td>{txn.bookingReference || '-'}</td>
                      <td>
                        <span className={`status-badge status-${bookingStatus.toLowerCase()}`}>
                          {BOOKING_STATUS_LABEL[bookingStatus] || bookingStatus.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>{formatPaidDateTime(txn.paidAt)}</td>
                      <td>₹{txn.amount || ADVANCE_AMOUNT}</td>
                      <td>
                        {txn.settled === true ? (
                          <span className="advance-pill settled small">✓ Paid to you</span>
                        ) : payable ? (
                          <span className="advance-pill pending small">Pending</span>
                        ) : (
                          <span className="advance-pill na small">Not payable</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}

export default SalonAdvancePayments;
